import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/translations';
import { useLanguage } from '../i18n/language';
import LanguageSelector from '../i18n/LanguageSelector';
import './MeetingLobby.css';

const IconMicOn = () => (
  <svg viewBox="0 0 24 24"><path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V20H9v2h6v-2h-2v-2.08A7 7 0 0 0 19 11h-2z"/></svg>
);
const IconMicOff = () => (
  <svg viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V20H9v2h6v-2h-2v-2.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/></svg>
);
const IconCamOn = () => (
  <svg viewBox="0 0 24 24"><path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/></svg>
);
const IconCamOff = () => (
  <svg viewBox="0 0 24 24"><path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/></svg>
);
const IconWarn = () => (
  <svg viewBox="0 0 24 24"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
);

/**
 * MeetingLobby
 *
 * Props:
 *   roomInfo   { meeting: { title, goal } }
 *   displayName  string
 *   photoData    string|null  — member's profile photo (data URL), if any
 *   onJoin({ stream, micOn, cameraOn })  — called when user clicks Join
 *   onBack()   — called when user clicks back/cancel
 */

const DEFAULT_AVATARS = [
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23FFD1DC'/%3E%3Ccircle cx='50' cy='40' r='20' fill='%23FFF'/%3E%3Cpath d='M30 80 Q50 60 70 80' stroke='%23FFF' stroke-width='10' fill='none'/%3E%3C/svg%3E",
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23AEC6CF'/%3E%3Ccircle cx='50' cy='40' r='20' fill='%23FFF'/%3E%3Cpath d='M30 80 Q50 60 70 80' stroke='%23FFF' stroke-width='10' fill='none'/%3E%3C/svg%3E",
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%2377DD77'/%3E%3Ccircle cx='50' cy='40' r='20' fill='%23FFF'/%3E%3Cpath d='M30 80 Q50 60 70 80' stroke='%23FFF' stroke-width='10' fill='none'/%3E%3C/svg%3E",
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23FDFD96'/%3E%3Ccircle cx='50' cy='40' r='20' fill='%23FFF'/%3E%3Cpath d='M30 80 Q50 60 70 80' stroke='%23FFF' stroke-width='10' fill='none'/%3E%3C/svg%3E"
];

export default function MeetingLobby({ roomInfo, displayName, photoData, onJoin, onBack, isGuest, guestName, onGuestNameChange, onSignIn }) {
  const t = useT();
  const videoRef  = useRef(null);
  const streamRef = useRef(null);

  const { toggleLanguage, language } = useLanguage();
  const fileInputRef = useRef(null);

  const [micOn,    setMicOn]    = useState(true);
  const [cameraOn, setCameraOn] = useState(false);
  const [permErr,  setPermErr]  = useState('');
  const [joining,  setJoining]  = useState(false);
  const [ready,    setReady]    = useState(false);   // stream acquired

  const [localName, setLocalName] = useState(guestName || displayName || '');
  const [localPhoto, setLocalPhoto] = useState(photoData || null);

  const initials = localName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLocalPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  // Track whether the stream was handed off to the parent — if so, don't stop tracks on unmount
  const transferredRef = useRef(false);

  // Acquire camera + mic on mount
  useEffect(() => {
    let cancelled = false;

    async function acquire() {
      try {
        // Start with audio only — camera is off by default
        const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
        if (cancelled) { stream.getTracks().forEach(tk => tk.stop()); return; }
        streamRef.current = stream;
        setReady(true);
        setPermErr('');
      } catch {
        if (!cancelled) setPermErr(t('lobby.permErrFull'));
      }
    }

    acquire();
    return () => {
      cancelled = true;
      // Release stream from video element so browser can collect it
      if (videoRef.current) videoRef.current.srcObject = null;
      // Only stop tracks if we didn't hand them off to the room page
      if (!transferredRef.current) {
        streamRef.current?.getTracks().forEach(tk => tk.stop());
      }
    };
  }, []);

  // Sync mic enabled state
  useEffect(() => {
    streamRef.current?.getAudioTracks().forEach(t => { t.enabled = micOn; });
  }, [micOn]);

  // Camera toggle: stop track (releases macOS green light) / re-acquire on enable
  useEffect(() => {
    if (cameraOn) {
      // Re-acquire camera if it was previously stopped
      const stream = streamRef.current;
      if (!stream) return;
      const hasLiveVideo = stream.getVideoTracks().some(t => t.readyState === 'live');
      if (hasLiveVideo) return; // already live, nothing to do

      navigator.mediaDevices.getUserMedia({ video: true }).then(newStream => {
        const track = newStream.getVideoTracks()[0];
        if (!track) return;
        stream.addTrack(track);
        if (videoRef.current) videoRef.current.srcObject = stream;
      }).catch(() => {});
    } else {
      // Stop and remove video track so macOS releases the camera light
      const stream = streamRef.current;
      if (!stream) return;
      stream.getVideoTracks().forEach(t => {
        stream.removeTrack(t);
        t.stop();
      });
      // Clear video element so browser releases the stream reference
      if (videoRef.current) videoRef.current.srcObject = null;
    }
  }, [cameraOn]);

  async function handleJoin() {
    setJoining(true);
    transferredRef.current = true; // don't stop tracks on unmount — ownership passes to room
    onJoin({ stream: streamRef.current, micOn, cameraOn, customName: localName, customPhotoData: localPhoto });
  }

  return (
    <div className="lobby">
      <div className="lobby__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 32px' }}>
        <p className="lobby__heading" style={{ margin: 0 }}>{t('lobby.readyToJoin')}</p>
        <LanguageSelector variant="full" placement="bottom-end" />
      </div>

      <div className="lobby__card">

        {/* ── Left: camera preview ── */}
        <div className="lobby__preview">
          <video
            ref={videoRef}
            className={`lobby__video${cameraOn && ready ? '' : ' lobby__video--hidden'}`}
            autoPlay
            playsInline
            muted
          />

          {(!cameraOn || !ready) && (
            <div className="lobby__avatar-preview">
              {localPhoto ? (
                <img className="lobby__avatar-photo" src={localPhoto} alt="" />
              ) : (
                <div className="lobby__avatar-circle">{initials}</div>
              )}
              <span className="lobby__avatar-label">
                {!ready ? t('lobby.startingCamera') : t('lobby.cameraOff')}
              </span>
            </div>
          )}

          {/* Mic / Camera quick-toggle overlay */}
          <div className="lobby__preview-controls">
            <button
              className={`lobby__icon-btn lobby__icon-btn--${micOn ? 'on' : 'off'}`}
              onClick={() => setMicOn(v => !v)}
              title={micOn ? t('room.mute') : t('room.unmute')}
            >
              {micOn ? <IconMicOn /> : <IconMicOff />}
            </button>
            <button
              className={`lobby__icon-btn lobby__icon-btn--${cameraOn ? 'on' : 'off'}`}
              onClick={() => setCameraOn(v => !v)}
              title={cameraOn ? t('room.stopVideo') : t('room.startVideo')}
            >
              {cameraOn ? <IconCamOn /> : <IconCamOff />}
            </button>
          </div>
        </div>

        {/* ── Right: info + controls ── */}
        <div className="lobby__side">

          {/* Meeting info */}
          <div className="lobby__meeting-info">
            <p className="lobby__meeting-label">{t('lobby.joiningMeeting')}</p>
            <h2 className="lobby__meeting-title">
              {roomInfo?.meeting?.title || t('meetings.meetingLabel')}
            </h2>
            {roomInfo?.meeting?.goal && (
              <p className="lobby__meeting-goal">{roomInfo.meeting.goal}</p>
            )}
          </div>

          {/* Device rows */}
          <div className="lobby__devices">
            {/* Microphone row */}
            <div className="lobby__device-row" onClick={() => setMicOn(v => !v)}>
              <div className="lobby__device-left">
                <div className={`lobby__device-icon lobby__device-icon--${micOn ? 'on' : 'off'}`}>
                  {micOn ? <IconMicOn /> : <IconMicOff />}
                </div>
                <div className="lobby__device-text">
                  <span className="lobby__device-name">{t('lobby.microphone')}</span>
                  <span className="lobby__device-state">{t(micOn ? 'lobby.micActive' : 'lobby.micMuted')}</span>
                </div>
              </div>
              <button
                className={`lobby__device-toggle lobby__device-toggle--${micOn ? 'on' : 'off'}`}
                onClick={e => { e.stopPropagation(); setMicOn(v => !v); }}
                aria-label="Toggle microphone"
              />
            </div>

            {/* Camera row */}
            <div className="lobby__device-row" onClick={() => setCameraOn(v => !v)}>
              <div className="lobby__device-left">
                <div className={`lobby__device-icon lobby__device-icon--${cameraOn ? 'on' : 'off'}`}>
                  {cameraOn ? <IconCamOn /> : <IconCamOff />}
                </div>
                <div className="lobby__device-text">
                  <span className="lobby__device-name">{t('lobby.camera')}</span>
                  <span className="lobby__device-state">{t(cameraOn ? 'lobby.camOn' : 'lobby.camOff')}</span>
                </div>
              </div>
              <button
                className={`lobby__device-toggle lobby__device-toggle--${cameraOn ? 'on' : 'off'}`}
                onClick={e => { e.stopPropagation(); setCameraOn(v => !v); }}
                aria-label="Toggle camera"
              />
            </div>
          </div>

          {/* Permission error */}
          {permErr && (
            <div className="lobby__error">
              <IconWarn />
              {permErr}
            </div>
          )}

          {/* Guest Name Input */}
          {/* Avatar Selection */}
          <div className="lobby__avatar-selection" style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
              {t('meetings.chooseAvatar') || 'اختر صورة للملف الشخصي'}
            </p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {DEFAULT_AVATARS.map((av, i) => (
                <img 
                  key={i} 
                  src={av} 
                  alt="Avatar" 
                  onClick={() => setLocalPhoto(av)}
                  style={{ 
                    width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
                    border: localPhoto === av ? '2px solid #2563eb' : '2px solid transparent',
                    transition: 'all 0.2s', objectFit: 'cover'
                  }} 
                />
              ))}
              <div 
                onClick={() => fileInputRef.current?.click()}
                style={{ 
                  width: 40, height: 40, borderRadius: '50%', background: '#e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', border: '1px dashed #94a3b8'
                }}
                title={t('meetings.uploadPhoto') || 'رفع صورة'}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="#475569"><path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/></svg>
              </div>
              <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handlePhotoUpload} />
            </div>
          </div>

          {/* Name Input */}
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
              {t('meetings.enterName') || 'الاسم (مطلوب)'}
            </p>
            <input 
              type="text" 
              className="form-input" 
              style={{ width: '100%', padding: '12px 16px', fontSize: '1rem', border: '1px solid #cfd9e0', borderRadius: 8, background: '#fff' }}
              placeholder={t('meetings.guestNamePlaceholder') || 'الاسم الكريم'}
              value={localName}
              onChange={e => {
                setLocalName(e.target.value);
                if (onGuestNameChange) onGuestNameChange(e.target.value);
              }}
            />
          </div>

          {/* Sign in instead of joining as guest */}
          {isGuest && onSignIn && (
            <button
              type="button"
              className="lobby__signin-link"
              onClick={onSignIn}
            >
              {t('lobby.signInInstead')}
            </button>
          )}

          {/* Join button */}
          <button
            className="lobby__join-btn"
            onClick={handleJoin}
            disabled={joining || (!ready && !permErr) || !localName.trim()}
          >
            {joining ? (
              <span className="lobby__join-btn--loading">
                <span className="lobby__spinner" /> {t('room.joining')}
              </span>
            ) : t('lobby.joinNow')}
          </button>

          {/* Back */}
          <button className="lobby__back" onClick={onBack}>
            {t('lobby.goBack')}
          </button>

        </div>
      </div>
    </div>
  );
}
